import { Copy } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import useOnboardingData from "@/store/onboarding-data";

const Installation = () => {
    const { storeData } = useOnboardingData();

    const handleCopyCss = () => {
        const copyText = `<link rel="stylesheet" href="${window.origin}/assets/css/shoe-einstein-chatbot.css">`;

        navigator.clipboard.writeText(copyText);
        toast.success("CSS code copied to clipboard");
    };

    const handleCopyJs = () => {
        const copyText = `<script>
        window.widgetConfig = {
            __chat_id__: "${storeData?.id}",
        };
    </script>
    <script src="${window.origin}/assets/js/shoe-einstein-bot.umd.js"></script>`;

        navigator.clipboard.writeText(copyText);
        toast.success("JS code copied to clipboard");
    };

    return (
        <div>
            <Card className="p-4 text-gray-700 shadow-none">
                <div className="flex items-center justify-between">
                    <div className="flex gap-10">
                        <h3 className="text-lg font-bold">Chat Bubble</h3>
                        <p>
                            Status: <span className="text-red-600">Inactive</span>
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Label htmlFor="toggle_order_tracking">Activate</Label>
                        <Switch id="toggle_order_tracking" />
                    </div>
                </div>
                <div>
                    <p>Display the Chatbot bubble on your store</p>
                </div>
                <Separator className="my-4" />

                <div>
                    <h3 className="text-lg font-bold">Install Via Javascript Code</h3>
                    <p className="mt-1 text-sm">
                        Our JS script allows you to load the Shoe Einstein Bubble on any page where the script is present. To install it, add the Shoe
                        Einstein JS script into the tag of your HTML page:
                    </p>
                    <Separator className="my-4" />
                    <p className="font-bold">1. Copy the code bolow and add in the head of your website HTML file</p>
                    <div className="relative mt-2 rounded-md border p-4 pt-8">
                        <Button
                            onClick={handleCopyCss}
                            size={"sm"}
                            variant={"outline"}
                            className="absolute right-0 top-0 rounded-br-none rounded-tl-none border-r-0 border-t-0"
                        >
                            <Copy />
                            Copy
                        </Button>
                        <code className="font-bold text-gray-500">{`<link rel="stylesheet" href="${window.origin}/assets/css/shoe-einstein-chatbot.css">`}</code>
                    </div>
                    <p className="mt-4 font-bold">1. Copy the code bolow and add in the end of the body of your website HTML file</p>
                    <div className="relative mt-2 rounded-md border p-4 pt-8 font-bold text-gray-500">
                        <Button
                            onClick={handleCopyJs}
                            size={"sm"}
                            variant={"outline"}
                            className="absolute right-0 top-0 rounded-br-none rounded-tl-none border-r-0 border-t-0"
                        >
                            <Copy />
                            Copy
                        </Button>
                        <code>
                            {`
                            <script>
                                window.widgetConfig = {
                                    __chat_id__: "${storeData?.id}"
                                };
                            </script>`}
                        </code>
                        <code className="mt-2 inline-block">
                            {`
                            <script src="${window.origin}/assets/js/shoe-einstein-bot.umd.js"></script>`}
                        </code>
                    </div>
                </div>
            </Card>

            {/* <Card className="mt-4 p-4 text-gray-700 shadow-none">
        <div className="flex items-center justify-between">
          <div className="flex gap-10">
            <h3 className="text-lg font-bold">Link to Chat</h3>
            <p>
              Status: <span className="text-red-600">Inactive</span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="toggle_order_tracking">Activate</Label>
            <Switch id="toggle_order_tracking" />
          </div>
        </div>
        <div>
          <p>Send customers to a standalone chat page</p>
        </div>
        <Separator className="my-4" />

        <div>
          <h3 className="text-lg font-bold">
            Use the following URL to link to the standalone chat page:
          </h3>
          <div className="relative">
            <Input
              type="text"
              placeholder="Enter Example"
              className="mt-4 min-h-12"
              value={jsCode}
              onChange={(e) => setJsCode(e.target.value)}
            />
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    className="absolute right-2 top-1/2 -translate-y-1/2 "
                    onClick={() => navigator.clipboard.writeText(jsCode)}
                  >
                    <Copy />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Add to library</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </Card>

      <Card className="mt-4 p-4 text-gray-700 shadow-none">
        <div className="flex items-center justify-between">
          <div className="flex gap-10">
            <h3 className="text-lg font-bold">Embedded Chat</h3>
            <p>
              Status: <span className="text-red-600">Inactive</span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="toggle_order_tracking">Activate</Label>
            <Switch id="toggle_order_tracking" />
          </div>
        </div>
        <div>
          <p>
            Embed the chat directly into a page, without the user having to
            click on a chat bubble to see the chat
          </p>
        </div>
        <Separator className="my-4" />

        <div>
          <h3 className="text-lg font-bold">
            To embed the chat into your page, add the following code into the
            HTML code of your page:
          </h3>
          <div className="relative">
            <Input
              type="text"
              placeholder="Enter Example"
              className="mt-4 min-h-12"
              value={jsCode}
              onChange={(e) => setJsCode(e.target.value)}
            />
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    className="absolute right-2 top-1/2 -translate-y-1/2 "
                    onClick={() => navigator.clipboard.writeText(jsCode)}
                  >
                    <Copy />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Add to library</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </Card> */}
            {/* <Card className="mt-4 flex items-center justify-end p-4">
        <Button>Save</Button>
      </Card> */}
        </div>
    );
};

export default Installation;
